import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import SEOHead from '../components/SEOHead';
import AdvisorDirectoryCard from '../components/AdvisorDirectoryCard';
import AdvisorInquiryForm from '../components/AdvisorInquiryForm';
import { useAuth } from '../contexts/AuthContext';
import {
  DOCUMENT,
  PAGE_CONTAINER_MAX_WIDTH,
  documentPageCanvasSx,
  documentPageContainerSx,
} from '../constants/documentTheme';
import { ADVISOR_DIRECTORY_SPECIALTIES, specialtyLabel } from '../constants/advisorDirectory';
import { partnerOutlineBtnSx, partnerOxbloodBtnSx } from '../components/PartnerListChrome';
import usePrivateTravelAvailable from '../hooks/usePrivateTravelAvailable';

const AdvisorDirectory = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [advisors, setAdvisors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qInput, setQInput] = useState('');
  const [locationInput, setLocationInput] = useState('');
  const [filters, setFilters] = useState({ q: '', location: '', specialty: '' });
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [inquiryTarget, setInquiryTarget] = useState(null);
  const [privateRequestBusy, setPrivateRequestBusy] = useState(false);
  const [privateRequestError, setPrivateRequestError] = useState('');
  const { available: privateTravelAvailable } = usePrivateTravelAvailable();

  const loadAdvisors = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.q.trim()) params.set('q', filters.q.trim());
      if (filters.location.trim()) params.set('location', filters.location.trim());
      if (filters.specialty) params.set('specialty', filters.specialty);
      const response = await api.get(`/advisors?${params.toString()}`);
      if (response.data?.success) {
        setAdvisors(response.data.advisors || []);
      }
    } catch (err) {
      console.error('Failed to load advisors:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadAdvisors();
  }, [loadAdvisors]);

  const applySearch = () => {
    setFilters((prev) => ({
      ...prev,
      q: qInput,
      location: locationInput,
    }));
  };

  const setSpecialtyFilter = (value) => {
    setFilters((prev) => ({
      ...prev,
      specialty: value,
    }));
  };

  const openContact = (advisor) => {
    setInquiryTarget({ ...advisor, privateRequest: false });
    setInquiryOpen(true);
  };

  const openPrivateRequest = async () => {
    if (!user) {
      navigate(`/login?redirect=${encodeURIComponent('/advisors')}`);
      return;
    }
    setPrivateRequestBusy(true);
    setPrivateRequestError('');
    try {
      const response = await api.get('/package-booking/private-request-target');
      if (!response.data?.available) {
        setPrivateRequestError(t('myInquiries.privateRequestUnavailable'));
        return;
      }
      setInquiryTarget({
        id: response.data.partnerId,
        companyName: response.data.partnerName,
        privateRequest: true,
      });
      setInquiryOpen(true);
    } catch (err) {
      console.error('Failed to open private request:', err);
      setPrivateRequestError(
        err.response?.data?.error || t('myInquiries.privateRequestUnavailable')
      );
    } finally {
      setPrivateRequestBusy(false);
    }
  };

  return (
    <Box sx={documentPageCanvasSx} data-testid="advisor-directory-page">
      <SEOHead
        title={t('advisorDirectory.seoTitle')}
        description={t('advisorDirectory.seoDescription')}
      />
      <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ ...documentPageContainerSx, py: 4 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'stretch', sm: 'flex-end' },
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
            mb: 3,
          }}
        >
          <Box sx={{ minWidth: 0, flex: '1 1 280px' }}>
            <Typography
              component="h1"
              sx={{
                fontFamily: DOCUMENT.display,
                fontSize: { xs: 32, md: 44 },
                lineHeight: 1.1,
                mb: 1,
              }}
            >
              {t('advisorDirectory.title')}
            </Typography>
            <Typography sx={{ color: DOCUMENT.ink2, fontSize: 15, maxWidth: 720 }}>
              {t('advisorDirectory.subtitle')}
            </Typography>
          </Box>
          {privateTravelAvailable ? (
            <Button
              onClick={openPrivateRequest}
              disabled={privateRequestBusy}
              sx={{ ...partnerOxbloodBtnSx, alignSelf: { xs: 'stretch', sm: 'flex-end' } }}
              data-testid="advisor-directory-private-request"
            >
              {t('advisorDirectory.privateRequest')}
            </Button>
          ) : null}
        </Box>
        {privateTravelAvailable && privateRequestError ? (
          <Typography sx={{ color: DOCUMENT.oxblood, fontSize: 13.5, mb: 2 }}>
            {privateRequestError}
          </Typography>
        ) : null}

        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            flexWrap: 'wrap',
            mb: 2,
            alignItems: 'flex-end',
          }}
        >
          <TextField
            label={t('advisorDirectory.searchLabel')}
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            size="small"
            sx={{ minWidth: 200, flex: 1 }}
          />
          <TextField
            label={t('advisorDirectory.locationLabel')}
            value={locationInput}
            onChange={(e) => setLocationInput(e.target.value)}
            size="small"
            sx={{ minWidth: 180, flex: 1 }}
          />
          <FormControl size="small" sx={{ minWidth: 180, flex: 1 }}>
            <InputLabel id="advisor-specialty-label">{t('advisorDirectory.specialtyLabel')}</InputLabel>
            <Select
              labelId="advisor-specialty-label"
              label={t('advisorDirectory.specialtyLabel')}
              value={filters.specialty}
              onChange={(e) => setSpecialtyFilter(e.target.value)}
              data-testid="advisor-specialty-filter"
            >
              <MenuItem value="">{t('advisorDirectory.allSpecialties')}</MenuItem>
              {ADVISOR_DIRECTORY_SPECIALTIES.map((item) => (
                <MenuItem key={item} value={item}>
                  {specialtyLabel(t, item)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button onClick={applySearch} sx={partnerOutlineBtnSx}>
            {t('advisorDirectory.search')}
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <CircularProgress sx={{ color: DOCUMENT.oxblood }} />
          </Box>
        ) : advisors.length === 0 ? (
          <Box
            sx={{
              bgcolor: DOCUMENT.surface,
              border: `1px solid ${DOCUMENT.ruleHard}`,
              p: { xs: 4, md: 5 },
              textAlign: 'center',
            }}
          >
            <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 24, mb: 1 }}>
              {t('advisorDirectory.emptyTitle')}
            </Typography>
            <Typography sx={{ color: DOCUMENT.ink3 }}>
              {t('advisorDirectory.emptyBody')}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2.5}>
            {advisors.map((advisor) => (
              <Grid item xs={12} sm={6} md={4} key={advisor.slug || advisor.id}>
                <AdvisorDirectoryCard
                  advisor={advisor}
                  testId={`advisor-card-${advisor.slug}`}
                  onContact={openContact}
                  onView={(item) => navigate(`/advisors/${item.slug}`)}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      <AdvisorInquiryForm
        open={inquiryOpen}
        onClose={() => {
          setInquiryOpen(false);
          setInquiryTarget(null);
        }}
        mode="general"
        partnerId={inquiryTarget?.id}
        partnerName={inquiryTarget?.companyName}
        fromDirectory={!inquiryTarget?.privateRequest}
        privateRequest={Boolean(inquiryTarget?.privateRequest)}
      />
    </Box>
  );
};

export default AdvisorDirectory;
